/**
 * Caseload Section Component for RNCM Productivity
 * 
 * Displays:
 * - Expected vs Actual caseload metrics
 * - Combined workload risk scores (60% caseload + 40% visits)
 * - Capacity utilization
 * - Hiring recommendations based on caseload
 * - Burnout risk integration
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Users, Target, TrendingUp, AlertTriangle, CheckCircle2, Activity,
  UserPlus, Heart, RefreshCw, ChevronRight, Info, MoreHorizontal, Pencil, Trash2
} from 'lucide-react';
import { API_URL } from './constants';
import { TeamBadge } from './Badges';

const getRiskColor = (risk) => {
  switch (risk?.toLowerCase()) {
    case 'critical': return 'bg-red-500 text-white';
    case 'high': return 'bg-orange-500 text-white';
    case 'medium': return 'bg-yellow-500 text-black';
    case 'low': return 'bg-green-500 text-white';
    case 'none': return 'bg-gray-300 text-gray-600';
    default: return 'bg-gray-400 text-white';
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'over': case 'high': case 'critical': return 'text-red-600';
    case 'optimal': return 'text-emerald-600';
    case 'under': return 'text-blue-600';
    case 'no_data': return 'text-gray-400';
    default: return 'text-gray-600';
  }
};

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'indigo' }) => (
  <Card className={`bg-gradient-to-br from-${color}-50 to-${color}-100 dark:from-${color}-900/20 dark:to-${color}-800/20 border-${color}-200 dark:border-${color}-800`}>
    <CardContent className="pt-4 pb-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-full bg-${color}-500/10`}>
          <Icon className={`h-5 w-5 text-${color}-500`} />
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center gap-1 text-xs">
          <TrendingUp className={`h-3 w-3 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
          <span className={trend > 0 ? 'text-green-600' : 'text-red-600'}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

export const CaseloadSection = ({ token, period, canEdit = true, onEditCapacity, onDeleteCapacity }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [caseloadData, setCaseloadData] = useState(null);
  const [burnoutData, setBurnoutData] = useState(null);
  const [hiringData, setHiringData] = useState(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [editForm, setEditForm] = useState({ expected_caseload: 15, actual_caseload: 0 });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clinicianToDelete, setClinicianToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCaseloadData = async () => {
    try {
      const periodParam = period ? `?period_start=${period}` : '';
      const [summaryRes, burnoutRes, hiringRes] = await Promise.all([
        fetch(`${API_URL}/api/caseload/capacity-projection${periodParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/caseload/burnout-integration${periodParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/caseload/hiring-recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setCaseloadData(data);
      }
      if (burnoutRes.ok) {
        const data = await burnoutRes.json();
        setBurnoutData(data);
      }
      if (hiringRes.ok) {
        const data = await hiringRes.json();
        setHiringData(data);
      }
    } catch (error) {
      toast.error('Failed to load caseload data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCaseloadData();
    }
  }, [token, period]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCaseloadData();
    toast.success('Caseload data refreshed');
  };

  // Edit handlers
  const handleEditClick = (clinician) => {
    setSelectedClinician(clinician);
    setEditForm({
      expected_caseload: clinician.expected_caseload || 15,
      actual_caseload: clinician.actual_caseload || 0
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedClinician) return;
    
    setSaving(true);
    try {
      // Direct API call to update caseload
      const response = await fetch(`${API_URL}/api/caseload/clinician/${encodeURIComponent(selectedClinician.clinician_name)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          expected_caseload: parseInt(editForm.expected_caseload),
          actual_caseload: parseInt(editForm.actual_caseload)
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to update caseload');
      }
      
      if (result.success) {
        toast.success(`Updated caseload for ${selectedClinician.clinician_name}`);
        setEditDialogOpen(false);
        fetchCaseloadData();
      } else {
        toast.error(result.error || 'Failed to update caseload');
      }
    } catch (error) {
      console.error('Caseload update error:', error);
      toast.error(error.message || 'Failed to save caseload changes');
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (clinician) => {
    setClinicianToDelete(clinician);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!clinicianToDelete) return;
    
    setDeleting(true);
    try {
      if (onDeleteCapacity) {
        await onDeleteCapacity(clinicianToDelete);
        toast.success(`Deleted caseload record for ${clinicianToDelete.clinician_name}`);
      } else {
        toast.info('Delete not available - use capacity table actions');
      }
      setDeleteDialogOpen(false);
      fetchCaseloadData();
    } catch (error) {
      toast.error('Failed to delete caseload record');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!caseloadData?.capacity) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No caseload data available</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add RNCM capacity data with patient assignments to see caseload metrics
          </p>
        </CardContent>
      </Card>
    );
  }

  const summary = caseloadData.capacity.summary;
  const clinicians = caseloadData.capacity.clinicians || [];
  const thresholds = caseloadData.thresholds || {};

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-500" />
            Caseload Expectations & Workload Analysis
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Expected vs actual caseload metrics with burnout risk integration
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Caseload"
          value={summary.total_actual_patients}
          subtitle={`of ${summary.total_expected_patients} expected`}
          icon={Users}
          color="indigo"
        />
        <MetricCard
          title="Caseload Utilization"
          value={`${summary.caseload_utilization_pct}%`}
          subtitle={summary.caseload_utilization_pct > 90 ? 'Near capacity' : 'Healthy range'}
          icon={Activity}
          color={summary.caseload_utilization_pct > 90 ? 'red' : 'emerald'}
        />
        <MetricCard
          title="Available Slots"
          value={summary.available_patient_slots}
          subtitle="Patient capacity remaining"
          icon={Target}
          color="blue"
        />
        <MetricCard
          title="Combined Utilization"
          value={`${summary.combined_utilization_pct}%`}
          subtitle="Caseload + Visits weighted"
          icon={TrendingUp}
          color={summary.combined_utilization_pct > 95 ? 'orange' : 'indigo'}
        />
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="py-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Workload Scoring:</strong> Combined risk uses 60% caseload + 40% visit load weighting.
              Expected caseload: <strong>{thresholds.expected_caseload || 15} patients/RNCM</strong>,
              Expected visits: <strong>{thresholds.expected_visits || 18} visits/week</strong>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="caseload" className="space-y-4">
        <TabsList>
          <TabsTrigger value="caseload">Caseload Details</TabsTrigger>
          <TabsTrigger value="burnout">Burnout Risk</TabsTrigger>
          <TabsTrigger value="hiring">Hiring Needs</TabsTrigger>
        </TabsList>

        {/* Caseload Details Tab */}
        <TabsContent value="caseload" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-4 w-4" />
                Per-Clinician Caseload & Workload Risk
              </CardTitle>
              <CardDescription>
                Expected vs actual caseload with combined workload scoring
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clinician</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead className="text-center">Actual Caseload</TableHead>
                    <TableHead className="text-center">Expected</TableHead>
                    <TableHead className="text-center">Visits</TableHead>
                    <TableHead className="text-center">Workload Score</TableHead>
                    <TableHead className="text-center">Risk Level</TableHead>
                    {canEdit && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clinicians.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        No clinician data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    clinicians.map((c, idx) => (
                      <TableRow key={idx} data-testid={`caseload-row-${idx}`}>
                        <TableCell className="font-medium">{c.clinician_name}</TableCell>
                        <TableCell><TeamBadge team={c.team} /></TableCell>
                        <TableCell className="text-center">
                          <span className={getStatusColor(c.caseload_status)}>
                            {c.actual_caseload}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {c.expected_caseload}
                        </TableCell>
                        <TableCell className="text-center">
                          {c.actual_visits} / {c.expected_visits}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Progress value={c.combined_score} className="w-16 h-2" />
                            <span className="text-xs font-medium">{c.combined_score}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={getRiskColor(c.workload_risk)}>
                            {c.workload_risk}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`caseload-actions-${idx}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditClick(c)} data-testid={`caseload-edit-${idx}`}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Caseload
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteClick(c)} 
                                  className="text-red-600"
                                  data-testid={`caseload-delete-${idx}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Record
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Capacity Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Optimal Capacity</p>
                    <p className="text-2xl font-bold text-emerald-600">{summary.optimal_count}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-emerald-500 opacity-70" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Staff within optimal caseload range
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Over Capacity</p>
                    <p className="text-2xl font-bold text-red-600">{summary.over_capacity_count}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-500 opacity-70" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Staff exceeding caseload limits
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Under Capacity</p>
                    <p className="text-2xl font-bold text-blue-600">{summary.under_capacity_count}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500 opacity-70" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Staff with available capacity
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Burnout Risk Tab */}
        <TabsContent value="burnout" className="space-y-4">
          {burnoutData?.agency_summary && (
            <>
              {/* Agency Summary */}
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Heart className="h-4 w-4 text-pink-500" />
                    Agency Burnout Risk Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{burnoutData.agency_summary.total_staff}</p>
                      <p className="text-xs text-muted-foreground">Total Staff</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{burnoutData.agency_summary.critical_risk_count}</p>
                      <p className="text-xs text-muted-foreground">Critical Risk</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">{burnoutData.agency_summary.high_risk_count}</p>
                      <p className="text-xs text-muted-foreground">High Risk</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{burnoutData.agency_summary.avg_combined_score}</p>
                      <p className="text-xs text-muted-foreground">Avg Risk Score</p>
                      <Badge className={getRiskColor(burnoutData.agency_summary.agency_risk_level)} variant="secondary">
                        {burnoutData.agency_summary.agency_risk_level}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* At-Risk Clinicians */}
              {burnoutData.agency_summary.at_risk_total > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-red-600 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      At-Risk Staff ({burnoutData.agency_summary.at_risk_total})
                    </CardTitle>
                    <CardDescription>
                      Clinicians with high or critical workload risk
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Clinician</TableHead>
                          <TableHead>Team</TableHead>
                          <TableHead className="text-center">Caseload Ratio</TableHead>
                          <TableHead className="text-center">Visit Ratio</TableHead>
                          <TableHead className="text-center">Combined Score</TableHead>
                          <TableHead className="text-center">Risk</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(burnoutData.clinicians || [])
                          .filter(c => c.risk_level === 'critical' || c.risk_level === 'high')
                          .map((c, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{c.clinician_name}</TableCell>
                              <TableCell><TeamBadge team={c.team} /></TableCell>
                              <TableCell className="text-center">{c.caseload_ratio}x</TableCell>
                              <TableCell className="text-center">{c.visit_ratio}x</TableCell>
                              <TableCell className="text-center font-semibold">{c.combined_score}</TableCell>
                              <TableCell className="text-center">
                                <Badge className={getRiskColor(c.risk_level)}>
                                  {c.risk_level}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {burnoutData.agency_summary.at_risk_total === 0 && (
                <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
                  <CardContent className="py-8 text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
                    <p className="font-medium text-emerald-700 dark:text-emerald-300">
                      No staff at high burnout risk
                    </p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                      All clinicians are operating within healthy workload parameters
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Hiring Needs Tab */}
        <TabsContent value="hiring" className="space-y-4">
          {hiringData && (
            <>
              {/* Current State */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-blue-500" />
                    Staffing Analysis Based on Caseload
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm text-muted-foreground">Current Census</p>
                      <p className="text-2xl font-bold">{hiringData.current_state?.census || 0}</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm text-muted-foreground">Current Staff</p>
                      <p className="text-2xl font-bold">{hiringData.current_state?.staff_count || 0}</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg Caseload</p>
                      <p className="text-2xl font-bold">{hiringData.current_state?.avg_caseload || 0}</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm text-muted-foreground">Optimal Target</p>
                      <p className="text-2xl font-bold text-blue-600">{hiringData.current_state?.optimal_caseload || 15}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recommendations */}
              {hiringData.recommendations && hiringData.recommendations.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Hiring Recommendations</CardTitle>
                    <CardDescription>
                      Based on current and projected caseload analysis
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {hiringData.recommendations.map((rec, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border ${
                            rec.priority === 'CRITICAL' ? 'bg-red-50 border-red-200 dark:bg-red-900/20' :
                            rec.priority === 'HIGH' ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20' :
                            rec.priority === 'MEDIUM' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20' :
                            'bg-blue-50 border-blue-200 dark:bg-blue-900/20'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  className={
                                    rec.priority === 'CRITICAL' ? 'bg-red-500' :
                                    rec.priority === 'HIGH' ? 'bg-orange-500' :
                                    rec.priority === 'MEDIUM' ? 'bg-yellow-500' : 'bg-blue-500'
                                  }
                                >
                                  {rec.priority}
                                </Badge>
                                <span className="font-medium">{rec.title}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{rec.reason}</p>
                            </div>
                            {rec.timeline && (
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {rec.timeline}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Projected State */}
              {hiringData.projected_state && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Projected Staffing Needs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-sm text-muted-foreground">Projected Census</p>
                        <p className="text-2xl font-bold">{hiringData.projected_state.projected_census}</p>
                      </div>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-sm text-muted-foreground">Required Staff</p>
                        <p className="text-2xl font-bold">{hiringData.projected_state.required_staff}</p>
                      </div>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-sm text-muted-foreground">Projected Avg Caseload</p>
                        <p className="text-2xl font-bold">{hiringData.projected_state.projected_avg_caseload}</p>
                      </div>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-sm text-muted-foreground">Staff Gap</p>
                        <p className={`text-2xl font-bold ${hiringData.projected_state.gap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {hiringData.projected_state.gap > 0 ? '+' : ''}{hiringData.projected_state.gap}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Caseload Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-caseload-dialog">
          <DialogHeader>
            <DialogTitle>Edit Caseload</DialogTitle>
            <DialogDescription>
              Update caseload expectations for {selectedClinician?.clinician_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="expected_caseload">Expected Caseload (patients)</Label>
              <Input
                id="expected_caseload"
                type="number"
                min="1"
                max="30"
                value={editForm.expected_caseload}
                onChange={(e) => setEditForm({ ...editForm, expected_caseload: parseInt(e.target.value) || 15 })}
                data-testid="expected-caseload-input"
              />
              <p className="text-xs text-muted-foreground">Default is 15 patients per RNCM</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="actual_caseload">Actual Caseload (patients)</Label>
              <Input
                id="actual_caseload"
                type="number"
                min="0"
                max="50"
                value={editForm.actual_caseload}
                onChange={(e) => setEditForm({ ...editForm, actual_caseload: parseInt(e.target.value) || 0 })}
                data-testid="actual-caseload-input"
              />
              <p className="text-xs text-muted-foreground">Current assigned patients</p>
            </div>
            {selectedClinician && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-muted-foreground">Current Status</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getRiskColor(selectedClinician.workload_risk)}>
                    {selectedClinician.workload_risk} risk
                  </Badge>
                  <span className="text-sm">Score: {selectedClinician.combined_score}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-caseload-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-caseload-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Caseload Record</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the caseload record for{' '}
              <strong>{clinicianToDelete?.clinician_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700 dark:text-red-300">
                  <p className="font-medium">This action cannot be undone.</p>
                  <p className="mt-1">The caseload data will be permanently removed from the capacity records.</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete} 
              disabled={deleting}
              data-testid="confirm-delete-caseload-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseloadSection;
